import React, { Component, Dispatch } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { SofaPanel, SofaButton } from '@sofology/sofologycomponents';
import { routes } from 'routes';

import './DeliveryDetailsPage.scss';

import { generateDispatchToPropsMap } from 'helpers/dispatchToPropsGenerator';
import {
    userHelper,
    scrollHelper,
    GtmCheckoutSteps,
    ExponeaCheckoutSteps,
    logsHelper,
    errorToBasketErrorModel,
    analyticsHelper
} from 'helpers';
import { NotificationTypes , IlpQuestionDescriptions } from 'models';
import { addNotification, AddNotificationFunc } from 'store/notifications/actions';
import { Loader, BasketModal } from 'components';
import { CheckoutAuthentication } from 'pages/DeliveryDetailsPage';
import { BasketDetailsModel } from 'models/Basket';
import { PaymentDetailsModel, PaymentTypes } from 'models/Payment';
import { AppState } from 'store';
import DeliveryDetailsForm, {
    DeliveryDetailsFormType,
} from './DeliveryDetailsForm/DeliveryDetailsForm';
import { DeliveryDetailsFormData } from 'models/Forms';
import { SofaFormComponent } from 'interfaces';
import {
    UpdateDeliveryDetailsFunc,
    updateDeliveryDetails,
    initCheckoutPage,
    InitCheckoutPageFunc,
    preprocessRedirectToNextStep,
    PreprocessRedirectToNextStepFunc
} from 'store/checkout/actions';

import {
    InitIfcApplicationDataFunc,
    initIfcApplicationData,
} from 'store/checkout/actions/finance';
import { setShowReservationWarning, SetShowReservationWarningFunc } from 'store/basket/actions';

interface OwnProps { }

interface StateProps {
    basket: BasketDetailsModel | null;
    paymentDetails: PaymentDetailsModel | null;
    isBasketLoading: boolean;
    isDeliveryDetailsUpdating: boolean;
    isDeliveryDetailsUpdatingFailed: boolean;
    isAuthProcess: boolean;
    ifcQuestionSet: IlpQuestionDescriptions | null;
    isCartValid: boolean;
    shouldShowReservationWarning: boolean;
}

interface DispatchProps {
    updateDeliveryDetails: UpdateDeliveryDetailsFunc;
    initCheckoutPage: InitCheckoutPageFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
    initIfcApplicationData: InitIfcApplicationDataFunc;
    setShowReservationWarning: SetShowReservationWarningFunc;
    addNotification: AddNotificationFunc;
}

interface OwnState {
    isContinueClickProcessing: boolean;
}

const POSTCODE_MISSING_ERROR_MESSAGE = 'Please, enter your delivery postcode';
const PAYMENT_METHOD_MISSING_ERROR_MESSAGE = 'Please, select a payment method';

class DeliveryDetailsPage extends Component<
StateProps & DispatchProps & OwnProps & RouteComponentProps,
OwnState
> {
    private deliveryDetailsForm = React.createRef<DeliveryDetailsFormType>();

    constructor(
        props: StateProps & DispatchProps & OwnProps & RouteComponentProps
    ) {
        super(props);

        this.state = {
            isContinueClickProcessing: false,
        };
    }

    componentDidMount() {
        this.props.initCheckoutPage();

        const isLoggedIn = userHelper.isUserLoggedIn();
        sendGooglePageViewAnalytics(isLoggedIn);
    }

    componentDidUpdate(
        prevProps: StateProps & DispatchProps & OwnProps & RouteComponentProps
    ) {
        if (
            this.isDeliveryDetailsUpdatingCompleted(prevProps)
        ) {
            this.setState({ isContinueClickProcessing: false }, () => {
                if (!this.props.isDeliveryDetailsUpdatingFailed) {
                    const hasValidationIssues = !this.props.isCartValid || this.props.shouldShowReservationWarning;

                    if (!hasValidationIssues) {
                        this.props.preprocessRedirectToNextStep(
                            () => this.props.history.push(routes.deliveryDate.path),
                            GtmCheckoutSteps.DeliveryDate,
                            ExponeaCheckoutSteps.DeliveryDetails
                        );
                    }
                } else {
                    this.validateForm();
                }
            });
        }

        if (this.redirectToBasketPageIfBasketIsEmpty(prevProps)) {
            return;
        }

        if (this.props.basket && this.props.ifcQuestionSet && !this.props.isDeliveryDetailsUpdating) {
            this.handleMissingCheckoutDetails();
        }

        if (
            prevProps.isAuthProcess &&
            !this.props.isAuthProcess &&
            userHelper.isUserLoggedIn()
        ) {
            scrollHelper.scrollToTopPage();
            sendGooglePageViewAnalytics(true);
        }
    }

    redirectToBasketPageIfBasketIsEmpty = (prevProps: StateProps): boolean => {
        if (!prevProps.basket && this.props.basket?.totalItems === 0) {
            this.props.history.push(routes.basket.path);

            return true;
        }

        return false;
    };

    handleContinueClick = () => {
        this.setState({ isContinueClickProcessing: true }, () => {
            this.validateForm()
                .then(isValid => {
                    if (isValid) {
                        const formComponent = this.deliveryDetailsForm
                            .current! as SofaFormComponent<DeliveryDetailsFormData>;
                        const formData = formComponent.getFormData();
                        this.props.initIfcApplicationData(
                            this.props.ifcQuestionSet!,
                            formData
                        );
                        this.props.updateDeliveryDetails(formData);
                    } else {
                        this.setState({ isContinueClickProcessing: false });
                    }
                })
                .catch(error => {
                    logsHelper.error(
                        errorToBasketErrorModel(
                            error,
                            'DeliveryDetailsPage:handleContinueClick'
                        )
                    );
                    this.setState({ isContinueClickProcessing: false });
                });
        });
    };

    handleReservationWarningClick = () => {
        this.props.setShowReservationWarning(false);
        this.props.history.push(routes.basket.path);
    };

    handleMissingCheckoutDetails = () => {
        const { basket, paymentDetails } = this.props;
        const mandatoryDetailsMissing = !basket?.deliveryAddress?.postcode || paymentDetails?.paymentType === PaymentTypes.None;

        if (!basket?.deliveryAddress?.postcode) {
            this.props.addNotification(
                POSTCODE_MISSING_ERROR_MESSAGE,
                NotificationTypes.Err
            );
        }

        if (paymentDetails?.paymentType === PaymentTypes.None) {
            this.props.addNotification(
                PAYMENT_METHOD_MISSING_ERROR_MESSAGE,
                NotificationTypes.Err
            );
        }

        if (mandatoryDetailsMissing) {
            this.props.history.push(routes.basket.path);
        }
    };

    render(): JSX.Element {
        const { basket, ifcQuestionSet, shouldShowReservationWarning, isDeliveryDetailsUpdating } = this.props;
        const isDeliveryDetailsPageInitializing = !basket || !ifcQuestionSet || isDeliveryDetailsUpdating;

        return (
            <div className='delivery-details-page'>
                {isDeliveryDetailsPageInitializing && this.renderLoader()}
                {!isDeliveryDetailsPageInitializing && this.renderContent()}
                <BasketModal open={shouldShowReservationWarning} onClick={this.handleReservationWarningClick} />
            </div>
        );
    }

    renderLoader(): JSX.Element {
        return <Loader show={true} />;
    }

    renderContent(): JSX.Element {
        return (
            <>
                {userHelper.isUserLoggedIn()
                    ? this.renderDeliveryDetails()
                    : this.renderAuthStep()}
            </>
        );
    }

    renderContinueButton(): JSX.Element {
        return (
            <div className='delivery-details-page__continue-button'>
                {(this.state.isContinueClickProcessing || this.props.isDeliveryDetailsUpdating) && (
                    <Loader show={true} overlay={true} />
                )}
                <SofaButton
                    text='Continue'
                    variant='primary'
                    onClick={this.handleContinueClick}
                />
            </div>
        );
    }

    renderDeliveryDetails(): JSX.Element {
        const { basket, isBasketLoading } = this.props;

        const shouldShowContinueButton = basket && !isBasketLoading;

        return (
            <>
                <SofaPanel>
                    <div className='delivery-details-page__delivery-info-wrapper'>
                        <div className='delivery-details-page__delivery-info-title'>
                            Your Details
                        </div>
                        <DeliveryDetailsForm ref={this.deliveryDetailsForm} />
                    </div>
                </SofaPanel>
                {shouldShowContinueButton && this.renderContinueButton()}
            </>
        );
    }

    renderAuthStep(): JSX.Element {
        return (
            <div className='checkout-authentication-container'>
                <CheckoutAuthentication pathname={this.getCurrentLocation()} />
            </div>
        );
    }

    isDeliveryDetailsUpdatingCompleted(prevProps: StateProps): boolean {
        return (
            this.state.isContinueClickProcessing &&
            prevProps.isDeliveryDetailsUpdating &&
            !this.props.isDeliveryDetailsUpdating
        );
    }

    async validateForm(): Promise<boolean> {
        let isValid = true;
        const formComponent = this.deliveryDetailsForm
            .current! as SofaFormComponent<DeliveryDetailsFormData>;
        const invalidControls = await formComponent.validate();

        if (invalidControls.length > 0) {
            isValid = false;

            let firstInvalidControl = invalidControls.min(c => c.index);
            if (firstInvalidControl.ref?.current) {
                scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
            }
        }

        return isValid;
    }

    getCurrentLocation(): string {
        return this.props.location.pathname;
    }
}

function sendGooglePageViewAnalytics(isLogged: boolean) {
    const pageViewOverrides = isLogged
        ? undefined
        : { category: 'login', subCategory: 'login', pageType: 'login' };

    analyticsHelper.sendToGoogleCustomPageViewMessage(routes.deliveryDetails.path, pageViewOverrides);
}

function mapStateToProps(state: AppState): StateProps {
    return {
        basket: state.basketState.basket,
        paymentDetails: state.paymentState.paymentDetails,
        isBasketLoading: state.basketState.isBasketLoading,
        isDeliveryDetailsUpdating:
            state.checkoutState.isDeliveryDetailsUpdating,
        isDeliveryDetailsUpdatingFailed: Boolean(
            state.checkoutState.deliveryDetailsUpdatingErrors
        ),
        isAuthProcess: state.checkoutState.isAuthProcess,
        isCartValid: state.basketState.validationResult.isValid,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet,
        shouldShowReservationWarning: state.basketState.shouldShowReservationWarning
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [
        updateDeliveryDetails,
        initCheckoutPage,
        preprocessRedirectToNextStep,
        initIfcApplicationData,
        setShowReservationWarning,
        addNotification
    ]);
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(DeliveryDetailsPage);
